/**
 * Feed Builder Admin — Master §22, §65.
 *
 * Lets a non-developer create/edit/deactivate a topic agent (feed
 * definition) through a form. The `np_topic_agents` collection is the
 * on-disk feed store; this page is a thin CRUD skin over the admin
 * `/admin/topic-agents` endpoints.
 *
 * §65 acceptance test: "Create a Kerala aviation news feed."
 * Fill this form with agent_id = kerala_aviation, categories = aviation,
 * keywords = kerala/cochin/kochi/trivandrum/calicut, destinations
 * airlinesairports + travelerpulse → Save → next pipeline pass distributes
 * matching stories. No developer needed.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Save, Loader2, Check, AlertTriangle, ChevronRight, X, Radio, Trash2,
} from "lucide-react";
import { npApi } from "./api";

const AUTOMATION_LEVELS = [
  { value: 1, label: "1 — Fully manual (log only)" },
  { value: 2, label: "2 — Auto-collect → draft-only" },
  { value: 3, label: "3 — Auto-publish (default)" },
  { value: 4, label: "4 — Auto-publish + auto-distribute" },
];

const GEO_SCOPES = ["global", "regional", "country", "state", "city"];

/** Textarea → array-of-lowercase-trimmed strings and back. */
function CsvArea({ value, onChange, placeholder, rows = 3, testId }) {
  const [text, setText] = useState((value || []).join(", "));
  useEffect(() => { setText((value || []).join(", ")); }, [value]);
  return (
    <textarea
      rows={rows}
      className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300 bg-white focus:border-blue-500 focus:outline-none"
      placeholder={placeholder}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const arr = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
        onChange(arr);
      }}
      data-testid={testId}
    />
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold mb-1">
        {label}
      </div>
      {children}
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </label>
  );
}

const EMPTY = {
  agent_id: "",
  name: "",
  description: "",
  active: true,
  categories: [],
  keywords_required_any: [],
  keywords_boost: [],
  keywords_exclude: [],
  geographic_scope: "global",
  languages: ["en"],
  min_confidence: 0.5,
  min_importance: 25,
  frequency_minutes: 60,
  destinations: [],
  human_review_categories: [],
  automation_level: 3,
};

function DestinationEditor({ destinations, onChange, siteOptions }) {
  const addDest = () => onChange([...destinations, { site_id: siteOptions[0]?.site_id || "", min_relevance: 50 }]);
  const rm = (i) => onChange(destinations.filter((_, idx) => idx !== i));
  const patch = (i, k, v) =>
    onChange(destinations.map((d, idx) => idx === i ? { ...d, [k]: v } : d));

  return (
    <div className="space-y-2" data-testid="destinations-editor">
      {destinations.map((d, i) => (
        <div key={i} className="flex flex-wrap gap-2 items-center bg-white border border-slate-200 rounded-lg p-2">
          <select
            value={d.site_id}
            onChange={(e) => patch(i, "site_id", e.target.value)}
            className="text-sm px-2 py-1 rounded border border-slate-300"
            data-testid={`destination-site-${i}`}
          >
            {siteOptions.map((s) => (
              <option key={s.site_id} value={s.site_id}>{s.site_name || s.site_id}</option>
            ))}
          </select>
          <label className="text-xs text-slate-600 flex items-center gap-1">
            min relevance
            <input
              type="number"
              min={0} max={100}
              value={d.min_relevance ?? 50}
              onChange={(e) => patch(i, "min_relevance", Number(e.target.value))}
              className="w-16 text-sm px-2 py-1 rounded border border-slate-300"
            />
          </label>
          <label className="text-xs text-slate-600 flex items-center gap-1">
            geo=
            <input
              type="text"
              placeholder="IN / US / *"
              value={d.require_geo || ""}
              onChange={(e) => patch(i, "require_geo", e.target.value || undefined)}
              className="w-16 text-sm px-2 py-1 rounded border border-slate-300 uppercase"
            />
          </label>
          <button
            type="button"
            className="ml-auto text-red-600 hover:bg-red-50 rounded p-1"
            onClick={() => rm(i)}
            aria-label="Remove"
            data-testid={`destination-remove-${i}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addDest}
        className="text-sm inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-dashed border-slate-300 text-slate-700 hover:bg-slate-50"
        data-testid="destination-add"
      >
        <Plus className="w-4 h-4" /> Add destination
      </button>
    </div>
  );
}

function AgentForm({ initial, siteOptions, onSaved, onCancel }) {
  const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const isEdit = !!initial?.agent_id;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setErr(""); setOk(false);
    try {
      if (isEdit) {
        await npApi.adminUpdateAgent(form.agent_id, form);
      } else {
        await npApi.adminCreateAgent(form);
      }
      setOk(true);
      onSaved && onSaved(form.agent_id);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-6" data-testid="feed-builder-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Agent ID" hint="Lower-case slug, letters/digits/underscore only. Immutable after save.">
          <input
            type="text"
            value={form.agent_id}
            onChange={(e) => update("agent_id", e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
            className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300"
            placeholder="e.g. agent_kerala_aviation"
            disabled={isEdit}
            data-testid="form-agent-id"
          />
        </Field>
        <Field label="Display name">
          <input
            type="text"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300"
            placeholder="e.g. Kerala Aviation News"
            data-testid="form-name"
          />
        </Field>
        <Field label="Description" hint="Editorial purpose. Shown in dashboards.">
          <textarea
            rows={2}
            value={form.description || ""}
            onChange={(e) => update("description", e.target.value)}
            className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300"
            data-testid="form-description"
          />
        </Field>
        <Field label="Categories" hint="One category path per comma, e.g. aviation, travel/aviation">
          <CsvArea value={form.categories} onChange={(v) => update("categories", v)}
                    placeholder="aviation, travel"
                    testId="form-categories" />
        </Field>
        <Field label="Required keywords (any-of)" hint="Article headline OR body must include at least one.">
          <CsvArea value={form.keywords_required_any}
                    onChange={(v) => update("keywords_required_any", v)}
                    placeholder="kerala, cochin, kochi, trivandrum, calicut"
                    testId="form-keywords-any" />
        </Field>
        <Field label="Boost keywords" hint="Increases importance score when present.">
          <CsvArea value={form.keywords_boost}
                    onChange={(v) => update("keywords_boost", v)}
                    placeholder="new route, airport expansion"
                    testId="form-keywords-boost" />
        </Field>
        <Field label="Exclude keywords" hint="Article is skipped if any of these appear.">
          <CsvArea value={form.keywords_exclude}
                    onChange={(v) => update("keywords_exclude", v)}
                    placeholder="celebrity tea, tea recipe"
                    testId="form-keywords-exclude" />
        </Field>
        <Field label="Human-review categories" hint="Events matching these categories go to editorial queue.">
          <CsvArea value={form.human_review_categories}
                    onChange={(v) => update("human_review_categories", v)}
                    placeholder="aviation/accidents, aviation/incidents"
                    testId="form-review-categories" />
        </Field>

        <Field label="Automation level">
          <select
            value={form.automation_level}
            onChange={(e) => update("automation_level", Number(e.target.value))}
            className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300"
            data-testid="form-automation-level"
          >
            {AUTOMATION_LEVELS.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Geographic scope">
          <select
            value={form.geographic_scope}
            onChange={(e) => update("geographic_scope", e.target.value)}
            className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300"
            data-testid="form-geo-scope"
          >
            {GEO_SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>

        <div className="grid grid-cols-3 gap-3 md:col-span-2">
          <Field label="Min confidence (0-1)">
            <input type="number" step="0.05" min={0} max={1}
                    value={form.min_confidence}
                    onChange={(e) => update("min_confidence", Number(e.target.value))}
                    className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300"
                    data-testid="form-min-confidence" />
          </Field>
          <Field label="Min importance (0-100)">
            <input type="number" min={0} max={100}
                    value={form.min_importance}
                    onChange={(e) => update("min_importance", Number(e.target.value))}
                    className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300"
                    data-testid="form-min-importance" />
          </Field>
          <Field label="Frequency (min)">
            <input type="number" min={5} max={1440}
                    value={form.frequency_minutes}
                    onChange={(e) => update("frequency_minutes", Number(e.target.value))}
                    className="w-full text-sm px-3 py-2 rounded-lg border border-slate-300"
                    data-testid="form-frequency" />
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field label="Destinations" hint="Which sister sites receive matching events.">
            <DestinationEditor
              destinations={form.destinations || []}
              onChange={(v) => update("destinations", v)}
              siteOptions={siteOptions}
            />
          </Field>
        </div>

        <label className="md:col-span-2 flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={!!form.active}
                  onChange={(e) => update("active", e.target.checked)}
                  data-testid="form-active" />
          Feed is active (next pipeline pass will process it)
        </label>
      </div>

      {err && (
        <div className="mt-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800 flex items-start gap-2" data-testid="form-error">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {err}
        </div>
      )}
      {ok && (
        <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800 flex items-start gap-2" data-testid="form-success">
          <Check className="w-4 h-4 shrink-0 mt-0.5" /> Saved. The next pipeline pass will pick this up.
        </div>
      )}

      <div className="mt-6 flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving || !form.agent_id || !form.name}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="form-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isEdit ? "Save changes" : "Create feed"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-sm text-slate-600 hover:text-slate-900"
          data-testid="form-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function NewsPulseAdminFeeds() {
  const [agents, setAgents] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // agent doc | "new" | null
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([npApi.topicAgents(), npApi.destinations()])
      .then(([a, d]) => { if (!cancelled) { setAgents(a); setDestinations(d); } })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshTick]);

  const siteOptions = useMemo(
    () => (destinations || []).map((d) => ({
      site_id: d.site_id, site_name: d.site_name || d.site_id
    })),
    [destinations]
  );

  const onSaved = () => {
    setEditing(null);
    setRefreshTick((n) => n + 1);
  };

  const deactivate = async (agent_id) => {
    if (!confirm(`Deactivate ${agent_id}? Existing events remain but no new ones will be created.`)) return;
    try {
      await npApi.adminDeactivateAgent(agent_id);
      setRefreshTick((n) => n + 1);
    } catch (e) {
      alert(`Failed: ${e.message}`);
    }
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-10" data-testid="newspulse-admin-feeds">
      <div className="pb-6 mb-6 border-b border-slate-200 flex items-start gap-4">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white">
          <Radio className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-slate-500">Feed Builder · Admin</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Feed Definitions</h1>
          <p className="text-slate-600 mt-1 max-w-2xl text-sm">
            Every row here is a topic agent. Create a new feed and the next
            ingestion pass automatically discovers, classifies, and distributes
            matching stories to the destinations you choose — no code deploy
            needed (Master §22, §65).
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
          data-testid="feed-builder-new"
        >
          <Plus className="w-4 h-4" /> New Feed
        </button>
      </div>

      {editing && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-slate-900 mb-3">
            {editing === "new" ? "Create feed" : `Edit ${editing.agent_id}`}
          </h2>
          <AgentForm
            initial={editing === "new" ? null : editing}
            siteOptions={siteOptions}
            onSaved={onSaved}
            onCancel={() => setEditing(null)}
          />
        </div>
      )}

      <h2 className="text-xl font-bold text-slate-900 mb-3">
        Existing feeds {agents.length ? `(${agents.length})` : ""}
      </h2>
      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : agents.length === 0 ? (
        <div className="text-slate-500 italic">No feeds yet. Click "New Feed".</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {agents.map((a) => (
            <div
              key={a.agent_id}
              className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow"
              data-testid={`feed-row-${a.agent_id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{a.agent_id}</div>
                  <h3 className="text-base font-bold text-slate-900">{a.name}</h3>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full ${
                  a.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"
                }`}>
                  {a.active ? "Active" : "Off"}
                </span>
              </div>
              {a.description && (
                <p className="mt-2 text-sm text-slate-600 line-clamp-2">{a.description}</p>
              )}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(a.categories || []).map((c) => (
                  <span key={c} className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                    {c}
                  </span>
                ))}
              </div>
              <div className="mt-3 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                <span>min conf {(a.min_confidence || 0).toFixed(2)}</span>
                <span>min imp {a.min_importance ?? 0}</span>
                <span>{(a.destinations || []).length} destinations</span>
                <span>every {a.frequency_minutes || 60}m</span>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <Link
                  to={`/agent/${a.agent_id}`}
                  className="text-xs text-slate-600 hover:text-blue-600 inline-flex items-center gap-1"
                >
                  View results <ChevronRight className="w-3 h-3" />
                </Link>
                <button
                  type="button"
                  onClick={() => setEditing(a)}
                  className="ml-auto text-sm px-3 py-1.5 rounded-lg border border-slate-300 hover:border-blue-400 hover:text-blue-600"
                  data-testid={`feed-edit-${a.agent_id}`}
                >
                  Edit
                </button>
                {a.active && (
                  <button
                    type="button"
                    onClick={() => deactivate(a.agent_id)}
                    className="text-sm px-3 py-1.5 rounded-lg text-red-600 hover:bg-red-50"
                    data-testid={`feed-deactivate-${a.agent_id}`}
                    title="Deactivate"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
